import axios, { Canceler } from 'axios';
import { action, computed, observable } from 'mobx';
import { persist } from 'mobx-persist';
import { DELIVERY_TYPES } from 'src/constants/deliveryTypes';
import { EVENTS } from 'src/constants/events';
import { E_MENU_TYPE } from 'src/constants/main';
import { requests, transformCategoryPagination, _transformCategory } from 'src/requests';
import client from 'src/services/client';
import logger from 'src/services/logger';
import { IBranch } from 'src/types/branch';
import { IOrder } from 'src/types/orders';
import { logError } from 'src/utils/helpers';
import { ICategory } from '../../types/products';
import { branchStore } from '../barnchStore';
import { preorderStore } from '../preorderStore';
import { userStore } from '../userStore';
import { appStore } from '../appStore';

type TCacheParameters = {
  delivery_type?: IOrder['delivery_type_code'];
  branch_id?: IBranch['id'];
  time?: number;
  global_category_id?: number;
  menu_update?: number;
  is_login?: boolean;
  is_donation?: 0 | 1;
};

export class ProductsStore {
  @observable public loading: boolean = false;
  // @persist
  @persist @observable public categoryId: ICategory['id'] | undefined = undefined;
  @persist('object') @observable public cashedParameters: any = {};
  @persist('list') @observable public categories: ICategory[] = [];
  @observable public searchIsActive: boolean = false;
  @observable public searchIsLoading: boolean = false;
  @observable public searchedCategories: ICategory[] | null = null;
  @persist('object') public cacheParameters: TCacheParameters = {};

  @computed get category(): ICategory | null {
    if (!this.categoryId) return null;
    const categoryIdx = this.categories.findIndex((category) => category.id === this.categoryId);
    return this.categories[categoryIdx];
  }
  // computed

  // private hasMenuUpdate = (newProps: any) => {
  //   try {
  //     return Object.entries(newProps).reduce((shouldBeUpdated, [key, value]) => {
  //       if (shouldBeUpdated) return shouldBeUpdated;
  //       return this.cashedParameters[key] !== value;
  //     }, false);
  //   } catch (error: any) {
  //     return true;
  //   }
  // };
  private stack: {
    [key: string]: Canceler;
  } = {};

  private hasMenuUpdate = (newProps: any) => {
    try {
      return Object.entries(newProps).reduce((shouldBeUpdated, [key, value]) => {
        if (shouldBeUpdated) return shouldBeUpdated;
        // @ts-ignore
        return this.cacheParameters[key] !== value;
      }, false);
    } catch (error) {
      return true;
    }
  };

  public fetchMenu = async () => {
    try {
      this.loading = true;

      // cancel all request if new one are called
      if (this.stack) Object.values(this.stack).map((canceller) => canceller('Cancelled'));

      const cacheParameters: TCacheParameters = {
        is_login: userStore.isLogin,

        // language: localeStore.language,
        branch_id: branchStore.branch.id,
        delivery_type: branchStore.delivery_type,
        time: preorderStore.preorder_unix,
        global_category_id: this.cashedParameters.global_category_id,
        menu_update: this.cashedParameters.menu_update,
        is_donation: 1,
      };

      if (appStore.mode === 'e-menu' && appStore?.settings?.donation?.enabled) {
        cacheParameters.is_donation = 0;
      }
      if (!cacheParameters.branch_id) {
        delete cacheParameters.branch_id;
      }
      if (!cacheParameters.global_category_id) {
        delete cacheParameters.global_category_id;
      }
      if (branchStore.order_type === E_MENU_TYPE.DINE_IN) {
        cacheParameters.delivery_type = DELIVERY_TYPES.DINE_IN;
      }

      const hasUpdates = this.hasMenuUpdate(cacheParameters);

      if (!hasUpdates) {
        // do not show loader if menu does not have updates
        // need to make silent update
        this.loading = false;
        // return null;
      } else {
        this.loading = true;
        // clear menu update to fetch menu
        cacheParameters.menu_update = 0;
      }

      // if any parameter not was changed need to make silent update
      // if menu_update or global_category_id was changed on server side api will return new menu
      // in other cases api will return empty menu
      const response = await client.get('/v4/categories/products', {
        params: cacheParameters,
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: new axios.CancelToken((c) => (this.stack.root = c)),
      });
      const { data: categories, global_category_id, menu_update } = response;

      // cashed parameters should be saved just if menu was fetched successfully
      if (!categories.length && menu_update !== this.cashedParameters.menu_update) {
        // if menu not changed
        this.loading = false;
        this.categories = [];
        return;
      }
      //  If saved categories is empty set categories from response
      this.categories = categories.map(_transformCategory);

      this.cacheParameters = {
        ...cacheParameters,
        menu_update,
        global_category_id,
      };

      this.categories.forEach((category) => {
        if (category.items.current_page >= category.items.last_page) return;
        this.additionalProductsForCategory(category, cacheParameters);
      });

      if (this.categories.length) {
        if (!this.categoryId) {
          // select first category if category now was selected before
          this.categoryId = this.categories[0].id;
        } else {
          // check if selected category exist, put first one if category missing
          const categoryIdx = this.categories.findIndex((category) => category.id === this.categoryId);
          this.categoryId = ~categoryIdx ? this.categories[categoryIdx].id : this.categories[0].id;
        }
      }
      if (!this.categoryId && this.categories.length) this.setCategory(this.categories[0].id);

      const content_items = this.category?.items.data.map((i) => i.name).join();
      const af_content_list = this.category?.items.data.map((i) => i.id);

      logger.logEvent(EVENTS.MENU_SHOW, {
        af_content_type: this.category?.name,
        af_content_list,
        content_items,
      });
      this.loading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') {
        return;
      }
      // logger.logEvent(EVENTS.SENTRY_ERROR_FETCHING_MENU, {}, error);
    } finally {
      this.loading = false;
    }
  };

  additionalProductsForCategory = async (category: ICategory, cacheParameters: TCacheParameters): Promise<void> => {
    try {
      let currentCategory: ICategory | undefined = undefined;
      const per_page = 10;
      if (category.items.current_page >= category.items.last_page) return;

      if (!cacheParameters.branch_id) {
        delete cacheParameters.branch_id;
      }
      if (!cacheParameters.global_category_id) {
        delete cacheParameters.global_category_id;
      }
      if (branchStore.order_type === E_MENU_TYPE.DINE_IN) {
        cacheParameters.delivery_type = DELIVERY_TYPES.DINE_IN;
      }

      const response: ICategory['items'] = await client.get(`/v4/categories/${category.id}/products`, {
        params: { ...cacheParameters, per_page, page: category.items.current_page + 1 },
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: new axios.CancelToken((c) => (this.stack[category.id] = c)),
      });
      delete this.stack[category.id];

      // update category items in the list
      this.categories = this.categories.map((_category) => {
        if (_category.id === category.id) {
          currentCategory = {
            ..._category,
            items: {
              ..._category.items,
              ...response,
              data: [..._category.items.data, ...transformCategoryPagination(response).data],
            },
          };
          return currentCategory;
        }
        return _category;
      });
      // Do not fetch next page is data length is less than per_page
      if (response.data.length < per_page || !currentCategory) return;

      await this.additionalProductsForCategory(currentCategory, cacheParameters);
    } catch (error: any) {
      // console.log('FETCH MENU CATEGORIES ERROR', category, category.items.current_page + 1);
      if (error.message === 'Cancelled') {
        return;
      }

      // logger.logEvent(EVENTS.SENTRY_ERROR_FETCHING_MENU_PART, {}, error);
    }
  };

  // actions
  @action public setCategory = async (id: ICategory['id']) => {
    try {
      const categoryIdx = this.categories.findIndex((category) => category.id === id);
      if (~categoryIdx) this.categoryId = this.categories[categoryIdx].id;
    } catch (error: any) {
      logError(error);
    }
  };

  // @action public fetchMenu = async () => {
  //   try {
  //     const cashedParameters = {
  //       language: localeStore.language,
  //       branch_id: branchStore.branch.id,
  //       delivery_type: branchStore.delivery_type,
  //       time:preorderStore.preorder_unix,
  //       global_category_id: this.cashedParameters.global_category_id,
  //       menu_update: this.cashedParameters.menu_update,
  //     };

  //     if (branchStore.order_type === E_MENU_TYPE.DINE_IN) {
  //       cashedParameters.delivery_type = DELIVERY_TYPES.DINE_IN;
  //     }

  //     const hasUpdates = this.hasMenuUpdate(cashedParameters);

  //     if (!hasUpdates) {
  //       // do not show loader if menu does not have updates
  //       // need to make silent update
  //       this.loading = false;
  //       // return null;
  //     } else {
  //       this.loading = true;
  //       // clear menu update to fetch menu
  //       this.cashedParameters.menu_update = 0;
  //     }

  //     // if any parameter not was changed need to make silent update
  //     // if menu_update or global_category_id was changed on server side api will return new menu
  //     // in other cases api will return empty menu
  //     const { categories, global_category_id, menu_update } = await requests.fetchMenu();

  //     cashedParameters.global_category_id = global_category_id;
  //     cashedParameters.menu_update = menu_update;

  //     // cashed parameters should be saved just if menu was fetched successfully
  //     this.cashedParameters = cashedParameters;
  //     if (!categories.length) {
  //       // if menu not changed
  //       this.loading = false;

  //       return;
  //     }

  //     this.categories = categories;
  //     if (this.categories.length) {
  //       if (!this.categoryId) {
  //         // select first category if category now was selected before
  //         this.categoryId = this.categories[0].id;
  //       } else {
  //         // check if selected category exist, put first one if category missing
  //         const categoryIdx = this.categories.findIndex((category) => category.id === this.categoryId);
  //         this.categoryId = ~categoryIdx ? this.categories[categoryIdx].id : this.categories[0].id;
  //       }
  //     }
  //     if (!this.categoryId && this.categories.length) this.setCategory(this.categories[0].id);

  //     const content_items = this.category?.items.data.map((i) => i.name).join();
  //     const af_content_list = this.category?.items.data.map((i) => i.id);

  //     FirebaseLogger.logEvent(SHOW_MENU, {
  //       af_content_type: this.category?.name,
  //       af_content_list,
  //       content_items,
  //     });
  //     this.loading = false;
  //   } catch (error: any) {
  //     this.loading = false;
  //     logError(error);
  //   }
  // };

  // @action public fetchMenuPart = async (id: ICategory['id'] | undefined) => {
  //   try {
  //     if (!id) return;

  //     const categoryIdx = this.categories.findIndex((category) => category.id === id);

  //     if (~categoryIdx) {
  //       const category = this.categories[categoryIdx];

  //       if (category.items.loading || category.items.current_page >= category.items.last_page) return;

  //       this.categories[categoryIdx].items.loading = true;

  //       const items = await requests.fetchMenuPart(id, category.items.current_page + 1);
  //       this.categories = this.categories.map((category) => {
  //         if (category.id !== id) return category;
  //         return {
  //           ...category,
  //           items: { ...items, data: [...category.items.data, ...items.data] },
  //         };
  //       });
  //     }
  //   } catch (error: any) {
  //     logError(error);
  //   }
  // };

  @action public toggleSearchIsActive = () => {
    this.searchIsActive = !this.searchIsActive;
    if (!this.searchIsActive) {
      this.searchIsLoading = false;
      this.searchedCategories = null;
    }
  };

  searchingTimeout: any = null;
  @action public searchMenuItems = (value: string) => {
    if (this.searchingTimeout) clearTimeout(this.searchingTimeout);

    // 750ms debounce
    this.searchingTimeout = setTimeout(async () => {
      try {
        if (!value || value.length <= 1) {
          return (this.searchedCategories = null);
        }

        this.searchIsLoading = true;
        const { categories } = await requests.fetchMenu(value);

        if (this.searchIsActive) this.searchedCategories = categories;

        this.searchIsLoading = false;

        const num_results_found = categories.reduce(
          (sum: number, category: any) => sum + category.items.data.length,
          0
        );
        logger.logEvent(EVENTS.MENU_SEARCH_PRODUCT, { search_term: value, num_results_found });
      } catch (error: any) {
        logError(error);
        this.searchIsLoading = false;
      }
    }, 750);
  };
}

const productsStore = new ProductsStore();

export { productsStore };
